import React, { useState, useMemo } from 'react';
import { FiUser, FiPlus, FiSearch } from 'react-icons/fi';
import { useDatabase } from '../../hooks/useDatabase';
import NativePersonForm from '../forms/NativePersonForm';

interface PersonMappingPromptProps {
  onSelect: (personId: string) => void;
}

/**
 * Prompt shown when the current user has not mapped themselves to a Person
 * in this workspace. Lets them search/select an existing person or create one.
 */
const PersonMappingPrompt: React.FC<PersonMappingPromptProps> = ({ onSelect }) => {
  const { database, applyCommand } = useDatabase();
  const [query, setQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const people = useMemo(() => {
    if (!database) return [];
    const m = database.people();
    return Object.entries(m).map(([id, p]) => ({
      id,
      name: (p as any).name(),
    }));
  }, [database]);

  const filtered = useMemo(() => {
    if (!query.trim()) return people;
    const q = query.toLowerCase();
    return people.filter(p => p.name.toLowerCase().includes(q));
  }, [people, query]);

  return (
    <div className="max-w-lg mx-auto py-12">
      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-8 text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-cm-bg-elevated border border-cm-border-mid flex items-center justify-center">
            <FiUser className="w-8 h-8 text-cm-accent-orange" />
          </div>
        </div>

        <div>
          <h2 className="text-xl font-mono font-semibold text-cm-text-primary">Which person are you?</h2>
          <p className="mt-2 text-sm text-cm-text-secondary">
            To see your work, teams, and interests, select the Person in this workspace that represents you.
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cm-text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people..."
            className="w-full pl-10 pr-4 py-2.5 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-orange focus:ring-cm-accent-orange"
          />
        </div>

        {/* People list */}
        {filtered.length > 0 ? (
          <div className="max-h-48 overflow-y-auto divide-y divide-cm-border-subtle border border-cm-border-subtle rounded-lg text-left">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => onSelect(p.id)}
                className="w-full px-4 py-3 text-sm text-cm-text-primary hover:bg-cm-bg-hover transition-colors text-left"
              >
                {p.name}
              </button>
            ))}
          </div>
        ) : people.length > 0 ? (
          <p className="text-sm text-cm-text-muted">No people match "{query}"</p>
        ) : (
          <p className="text-sm text-cm-text-muted">No people exist in this workspace yet.</p>
        )}

        {/* Create new person */}
        <div className="border-t border-cm-border-subtle pt-4">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg border border-cm-border-subtle text-sm text-cm-text-secondary hover:text-cm-text-primary hover:border-cm-border-mid transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Create a new person
          </button>
        </div>
      </div>

      <NativePersonForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={applyCommand}
      />
    </div>
  );
};

export default PersonMappingPrompt;
